import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Map as MapIcon,
  Sparkles,
  Trash2,
  FileText,
  Share2,
  Link2,
  Pencil,
  X,
  Check,
  ChevronDown,
  StickyNote,
  Archive,
  ArchiveRestore,
  LayoutGrid,
  Home,
  Users,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  fetchProject,
  fetchProjectImages,
  updateImage,
  renameCategory,
  deleteCategoryApi,
  updateProject,
  fetchMoodboards,
  createMoodboard,
  updateMoodboard,
  deleteMoodboard,
  generateAiMoodboard,
  fetchFloorplans,
  createFloorplan,
  updateFloorplan,
  deleteFloorplan,
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadImage,
  addTab,
  updateTab,
  deleteTab,
  reorderTabs,
  addRoom,
  updateRoom,
  deleteRoom,
  archiveProject,
  unarchiveProject,
} from "@/api";
import ImageUploader from "@/components/ImageUploader";
import ImageCard from "@/components/ImageCard";
import ImageDetailDialog from "@/components/ImageDetailDialog";
import ConfirmDeleteImageDialog from "@/components/ConfirmDeleteImageDialog";
import FloorplanEditor from "@/components/FloorplanEditor";
import ExportChecklistDialog from "@/components/ExportChecklistDialog";
import ShareDialog from "@/components/ShareDialog";
import CollaboratorsDialog from "@/components/CollaboratorsDialog";
import DropZone, { UrlImportDialog } from "@/components/DropZone";
import AiMoodboardDialog from "@/components/AiMoodboardDialog";
import TextPromptDialog from "@/components/TextPromptDialog";
import RoomMoodboardBuilderDialog from "@/components/RoomMoodboardBuilderDialog";
import { useConfirm } from "@/components/ConfirmDialog";
import { categoryLabel, formatPrice, readFileAsDataURL, downsizeImage, isPdf, openInNewTab } from "@/lib/utils";
import { exportProjectPDF } from "@/lib/pdfExport";

export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const confirm = useConfirm();
  const [project, setProject] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [uploadCategory, setUploadCategory] = useState("inspiration");
  const [selected, setSelected] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);
  const [draggedOverPill, setDraggedOverPill] = useState(null);
  const [draggedOverRoom, setDraggedOverRoom] = useState(null);
  // Inline text-prompt dialog (replaces window.prompt which is blocked in
  // the preview iframe).
  const [promptDialog, setPromptDialog] = useState(null);
  // Per-room moodboard builder
  const [roomMoodboardFor, setRoomMoodboardFor] = useState(null);
  // Archive confirmation
  const [archiveBusy, setArchiveBusy] = useState(false);

  // Sync uploadCategory with active filter when filter is a concrete category
  useEffect(() => {
    if (filter && filter !== "all") setUploadCategory(filter);
  }, [filter]);

  const projectCategories = useMemo(() => {
    const cats = project?.categories;
    if (Array.isArray(cats) && cats.length > 0) return cats;
    return ["inspiration", "site", "product", "material", "floorplan"];
  }, [project]);

  // Combined rooms chips:
  //   - Canonical rooms from `project.rooms[]` (manually managed, can be
  //     renamed/deleted/moodboarded)
  //   - Legacy AI-inferred `room_type` values that don't have a canonical
  //     room (still shown as filter chips so existing data isn't hidden)
  // Returns `[{ kind: "canonical"|"legacy", id, name, key, label, count }]`
  const projectRooms = useMemo(() => {
    const canonical = (project?.rooms || []).map((r) => ({
      kind: "canonical",
      id: r.id,
      name: r.name,
      key: `room:${r.id}`,
      label: r.name,
      count: 0,
    }));
    const NULLISH = new Set(["", "none", "n/a", "unknown", "null", "other"]);
    const canonicalByLower = new Map(
      canonical.map((c) => [c.name.trim().toLowerCase(), c]),
    );
    const legacyMap = new Map();
    images.forEach((img) => {
      if (img.room_id) {
        const hit = canonical.find((c) => c.id === img.room_id);
        if (hit) {
          hit.count += 1;
          return;
        }
      }
      const raw = (img.room_type || "").trim().toLowerCase();
      if (NULLISH.has(raw)) return;
      const can = canonicalByLower.get(raw);
      if (can) {
        can.count += 1;
        return;
      }
      legacyMap.set(raw, (legacyMap.get(raw) || 0) + 1);
    });
    const legacy = Array.from(legacyMap.entries()).map(([key, count]) => ({
      kind: "legacy",
      key: `legacy:${key}`,
      legacyKey: key,
      label: key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      count,
    }));
    return [
      ...canonical.sort((a, b) =>
        b.count - a.count || a.label.localeCompare(b.label),
      ),
      ...legacy.sort((a, b) =>
        b.count - a.count || a.label.localeCompare(b.label),
      ),
    ];
  }, [project, images]);

  // Active room filter (multi-select). Empty Set == "All rooms".
  const [roomFilter, setRoomFilter] = useState(() => new Set());

  function toggleRoom(key) {
    setRoomFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function clearRooms() {
    setRoomFilter(new Set());
  }

  async function handleMoveImage(img, newCategory) {
    if (!newCategory || newCategory === img.category) return;
    try {
      const updated = await updateImage(img.id, { category: newCategory });
      setImages((prev) => prev.map((p) => (p.id === img.id ? { ...p, ...updated } : p)));
      toast.success(`Moved to ${categoryLabel(newCategory)}`);
    } catch (e) {
      console.error(e);
      toast.error("Move failed");
    }
  }

  function handleAddCategory() {
    setPromptDialog({
      title: "New category",
      description: "Group your files by purpose (e.g. lighting, finishes).",
      initial: "",
      placeholder: "Category name",
      submitLabel: "Add category",
      testid: "add-category-prompt",
      onSubmit: async (raw) => {
        const name = (raw || "").trim().toLowerCase();
        if (!name) return;
        if (projectCategories.includes(name)) {
          toast.error("That category already exists");
          throw new Error("dup");
        }
        try {
          const updated = await updateProject(projectId, {
            categories: [...projectCategories, name],
          });
          setProject(updated);
          toast.success(`Category "${categoryLabel(name)}" added`);
        } catch {
          toast.error("Failed to add category");
          throw new Error("fail");
        }
      },
    });
  }

  function handleRenameCategory(oldName) {
    setPromptDialog({
      title: `Rename "${categoryLabel(oldName)}"`,
      description: "Pick a new name. Files keep their place in the new category.",
      initial: oldName,
      placeholder: "Category name",
      submitLabel: "Rename",
      testid: "rename-category-prompt",
      onSubmit: async (raw) => {
        const next = (raw || "").trim().toLowerCase();
        if (!next || next === oldName) return;
        try {
          const updated = await renameCategory(projectId, oldName, next);
          setProject(updated);
          await refreshImages();
          if (filter === oldName) setFilter(next);
          if (uploadCategory === oldName) setUploadCategory(next);
          toast.success("Category renamed");
        } catch (e) {
          const msg = e?.response?.data?.detail || "Rename failed";
          toast.error(typeof msg === "string" ? msg : "Rename failed");
          throw e;
        }
      },
    });
  }

  // ---- Room CRUD (manual) ----
  function handleAddRoom() {
    setPromptDialog({
      title: "Add room",
      description:
        "Rooms help organise files by space. Link files via the card menu or drag them onto a room chip.",
      initial: "",
      placeholder: "e.g. Kitchen, Master Bedroom, Hallway",
      submitLabel: "Add room",
      testid: "add-room-prompt",
      onSubmit: async (raw) => {
        const name = (raw || "").trim();
        if (!name) return;
        try {
          const updated = await addRoom(projectId, { name });
          setProject(updated);
          toast.success(`Room "${name}" added`);
        } catch (e) {
          const msg = e?.response?.data?.detail || "Failed to add room";
          toast.error(typeof msg === "string" ? msg : "Failed to add room");
          throw e;
        }
      },
    });
  }

  function handleRenameRoom(room) {
    setPromptDialog({
      title: `Rename "${room.name}"`,
      description: "Linked files follow the room automatically.",
      initial: room.name,
      placeholder: "Room name",
      submitLabel: "Rename",
      testid: "rename-room-prompt",
      onSubmit: async (raw) => {
        const next = (raw || "").trim();
        if (!next || next === room.name) return;
        try {
          const updated = await updateRoom(projectId, room.id, { name: next });
          setProject(updated);
          toast.success("Room renamed");
        } catch (e) {
          const msg = e?.response?.data?.detail || "Rename failed";
          toast.error(typeof msg === "string" ? msg : "Rename failed");
          throw e;
        }
      },
    });
  }

  async function handleDeleteRoom(room) {
    // Files keep their data; only their `room_id` is unlinked server-side.
    try {
      const updated = await deleteRoom(projectId, room.id);
      setProject(updated);
      // Drop this canonical room from the active filter
      setRoomFilter((prev) => {
        if (!prev.has(`room:${room.id}`)) return prev;
        const next = new Set(prev);
        next.delete(`room:${room.id}`);
        return next;
      });
      // Refresh images so the unlinked room_id reflects locally
      await refreshImages();
      toast.success(`Room "${room.name}" deleted — linked files preserved`);
    } catch (e) {
      toast.error("Failed to delete room");
    }
  }

  async function handleMoveImageToRoom(img, roomId) {
    if ((img.room_id || null) === (roomId || null)) return;
    try {
      const updated = await updateImage(img.id, { room_id: roomId });
      setImages((prev) =>
        prev.map((p) => (p.id === img.id ? { ...p, ...updated } : p)),
      );
      if (!roomId) toast.success("Unlinked from room");
      else {
        const room = (project?.rooms || []).find((r) => r.id === roomId);
        toast.success(`Linked to ${room?.name || "room"}`);
      }
    } catch {
      toast.error("Move failed");
    }
  }

  function onRoomDragOver(e, chip) {
    if (chip.kind !== "canonical") return;
    if (!e.dataTransfer.types.includes("application/x-atelier-image-id")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDraggedOverRoom(chip.id);
  }
  function onRoomDragLeave() {
    setDraggedOverRoom(null);
  }
  function onRoomDrop(e, chip) {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverRoom(null);
    if (chip.kind !== "canonical") return;
    const id = e.dataTransfer.getData("application/x-atelier-image-id");
    if (!id) return;
    const img = images.find((i) => i.id === id);
    if (img) handleMoveImageToRoom(img, chip.id);
  }

  function handleBuildRoomMoodboard(room) {
    setRoomMoodboardFor(room);
  }
  function roomImageCount(roomId) {
    const r = (project?.rooms || []).find((x) => x.id === roomId);
    const nameLc = (r?.name || "").trim().toLowerCase();
    return images.filter((i) => {
      if (i.room_id === roomId) return true;
      const rt = (i.room_type || "").trim().toLowerCase();
      return rt && rt === nameLc;
    }).length;
  }

  // ---- Archive / Unarchive ----
  async function handleArchive() {
    if (archiveBusy) return;
    setArchiveBusy(true);
    try {
      const updated = await archiveProject(projectId);
      setProject(updated);
      toast.success("Project archived");
    } catch {
      toast.error("Failed to archive");
    } finally {
      setArchiveBusy(false);
    }
  }
  async function handleUnarchive() {
    if (archiveBusy) return;
    setArchiveBusy(true);
    try {
      const updated = await unarchiveProject(projectId);
      setProject(updated);
      toast.success("Project restored");
    } catch {
      toast.error("Failed to restore");
    } finally {
      setArchiveBusy(false);
    }
  }

  async function handleDeleteCategory(name) {
    if (name === "inspiration") {
      toast.error("'Inspiration' is the fallback and can't be removed");
      return;
    }
    const count = images.filter((i) => i.category === name).length;
    const msg = count
      ? `Delete "${categoryLabel(name)}"? ${count} image${
          count === 1 ? "" : "s"
        } will move to Inspiration.`
      : `Delete "${categoryLabel(name)}"?`;
    const ok = await confirm({
      title: `Delete "${categoryLabel(name)}"?`,
      description: count
        ? `${count} image${count === 1 ? "" : "s"} in this category will move to Inspiration.`
        : "This category is empty — it'll just be removed.",
      confirmLabel: "Delete category",
      destructive: true,
    });
    if (!ok) return;
    try {
      const updated = await deleteCategoryApi(projectId, name);
      setProject(updated);
      await refreshImages();
      if (filter === name) setFilter("all");
      if (uploadCategory === name) setUploadCategory("inspiration");
      toast.success("Category removed");
    } catch {
      toast.error("Failed to delete category");
    }
  }

  // ---- drag a card onto a category pill to move ----
  function onPillDragOver(e, cat) {
    if (!e.dataTransfer.types.includes("application/x-atelier-image-id")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDraggedOverPill(cat);
  }
  function onPillDragLeave() {
    setDraggedOverPill(null);
  }
  function onPillDrop(e, cat) {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverPill(null);
    const id = e.dataTransfer.getData("application/x-atelier-image-id");
    if (!id) return;
    const img = images.find((i) => i.id === id);
    if (img) handleMoveImage(img, cat);
  }

  async function handleExportProjectPDF(sections) {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const needsMoodboards = sections.includes("moodboards");
      const needsFloorplans = sections.includes("floorplans");
      const needsProducts = sections.includes("products");
      const [moodboards, floorplans, products] = await Promise.all([
        needsMoodboards ? fetchMoodboards(projectId) : Promise.resolve([]),
        needsFloorplans ? fetchFloorplans(projectId) : Promise.resolve([]),
        needsProducts ? fetchProducts(projectId) : Promise.resolve([]),
      ]);
      await exportProjectPDF({
        project,
        images,
        moodboards,
        floorplans,
        products,
        sections,
      });
      toast.success("PDF exported");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    } finally {
      setPdfBusy(false);
    }
  }

  async function refreshImages() {
    const list = await fetchProjectImages(projectId);
    setImages(list);
  }

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [p, imgs] = await Promise.all([
          fetchProject(projectId),
          fetchProjectImages(projectId),
        ]);
        if (!active) return;
        setProject(p);
        setImages(imgs);
      } catch (e) {
        toast.error("Could not load project");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [projectId]);

  const filteredImages = useMemo(() => {
    let list = filter === "all" ? images : images.filter((i) => i.category === filter);
    if (roomFilter.size > 0) {
      // Pre-compute lookups for the active filter set
      const canonicalIds = new Set();
      const canonicalLowerNames = new Set();
      const legacyKeys = new Set();
      roomFilter.forEach((key) => {
        if (key.startsWith("room:")) {
          const id = key.slice(5);
          canonicalIds.add(id);
          const r = (project?.rooms || []).find((x) => x.id === id);
          if (r?.name) canonicalLowerNames.add(r.name.trim().toLowerCase());
        } else if (key.startsWith("legacy:")) {
          legacyKeys.add(key.slice(7));
        }
      });
      list = list.filter((i) => {
        if (i.room_id && canonicalIds.has(i.room_id)) return true;
        const rt = (i.room_type || "").trim().toLowerCase();
        if (rt && canonicalLowerNames.has(rt)) return true;
        if (rt && legacyKeys.has(rt)) return true;
        return false;
      });
    }
    return list;
  }, [filter, images, roomFilter, project]);

  function handleUploaded(img) {
    setImages((prev) => {
      const exists = prev.find((p) => p.id === img.id);
      if (exists) return prev.map((p) => (p.id === img.id ? { ...p, ...img } : p));
      return [img, ...prev];
    });
    // If the upload introduced a new category (e.g. 'documents', 'cad'),
    // merge it into the local project so the filter pill renders without reload.
    if (img?.category) {
      setProject((prev) => {
        if (!prev) return prev;
        const cats = prev.categories || [];
        if (cats.includes(img.category)) return prev;
        return { ...prev, categories: [...cats, img.category] };
      });
    }
  }

  async function handleDeleteImage(img) {
    setPendingDelete(img);
  }

  function handleDeletedImage(img) {
    setImages((prev) => prev.filter((p) => p.id !== img.id));
    setSelected((s) => (s && s.id === img.id ? null : s));
  }

  function handleUpdated(img) {
    setImages((prev) => prev.map((p) => (p.id === img.id ? { ...p, ...img } : p)));
    setSelected((s) => (s && s.id === img.id ? { ...s, ...img } : s));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-stone-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <p className="text-stone-500">Project not found.</p>
        <Link to="/studio" className="text-stone-900 underline mt-3 inline-block">
          Back to studio
        </Link>
      </div>
    );
  }

  return (
    <DropZone
      projectId={projectId}
      category={uploadCategory}
      onUploaded={handleUploaded}
      className="min-h-screen"
    >
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-12" data-testid="project-workspace">
      <Link
        to="/studio"
        className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 text-sm mb-6"
        data-testid="back-to-projects"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        Studio
      </Link>

      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: project.color_tag || "#8A9A86" }}
            />
            <span className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">
              {project.client || "Project"}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-stone-900">
            {project.name}
          </h1>
          {project.description && (
            <p className="text-stone-500 mt-2 max-w-2xl">{project.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2" data-testid="project-actions">
          {project.archived ? (
            <Button
              variant="outline"
              onClick={handleUnarchive}
              disabled={archiveBusy}
              data-testid="project-unarchive-button"
              className="rounded-full"
            >
              {archiveBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArchiveRestore className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              Restore
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleArchive}
              disabled={archiveBusy}
              data-testid="project-archive-button"
              className="rounded-full"
            >
              {archiveBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Archive className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              Archive
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setPdfDialogOpen(true)}
            disabled={pdfBusy}
            data-testid="project-export-pdf"
            className="rounded-full"
          >
            {pdfBusy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
            )}
            Export PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => setCollabOpen(true)}
            data-testid="project-collaborators-button"
            className="rounded-full"
          >
            <Users className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Collaborators
          </Button>
          <Button
            variant="outline"
            onClick={() => setShareOpen(true)}
            data-testid="project-share-button"
            className="rounded-full"
          >
            <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Share
          </Button>
        </div>
      </div>

      {project.archived && (
        <div
          className="mb-6 rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-center gap-3"
          data-testid="archived-banner"
        >
          <Archive className="w-4 h-4 text-amber-700" strokeWidth={1.5} />
          <p className="text-sm text-amber-800">
            This project is archived. It's hidden from the active studio view —
            restore it to keep editing.
          </p>
        </div>
      )}

      <ProjectTabs
        project={project}
        projectId={projectId}
        onProjectChange={setProject}
        images={images}
        // Library props
        filter={filter}
        setFilter={setFilter}
        uploadCategory={uploadCategory}
        setUploadCategory={setUploadCategory}
        projectCategories={projectCategories}
        projectRooms={projectRooms}
        roomFilter={roomFilter}
        toggleRoom={toggleRoom}
        clearRooms={clearRooms}
        filteredImages={filteredImages}
        draggedOverPill={draggedOverPill}
        onPillDragOver={onPillDragOver}
        onPillDragLeave={onPillDragLeave}
        onPillDrop={onPillDrop}
        handleAddCategory={handleAddCategory}
        handleRenameCategory={handleRenameCategory}
        handleDeleteCategory={handleDeleteCategory}
        handleMoveImage={handleMoveImage}
        handleMoveImageToRoom={handleMoveImageToRoom}
        handleUploaded={handleUploaded}
        setSelected={setSelected}
        handleDeleteImage={handleDeleteImage}
        selected={selected}
        pendingDelete={pendingDelete}
        setPendingDelete={setPendingDelete}
        handleUpdated={handleUpdated}
        handleDeletedImage={handleDeletedImage}
        refreshImages={refreshImages}
        handleAddRoom={handleAddRoom}
        handleRenameRoom={handleRenameRoom}
        handleDeleteRoom={handleDeleteRoom}
        handleBuildRoomMoodboard={handleBuildRoomMoodboard}
        draggedOverRoom={draggedOverRoom}
        onRoomDragOver={onRoomDragOver}
        onRoomDragLeave={onRoomDragLeave}
        onRoomDrop={onRoomDrop}
      />

      <ExportChecklistDialog
        open={pdfDialogOpen}
        onOpenChange={setPdfDialogOpen}
        title="Export project PDF"
        sections={[
          {
            key: "cover",
            label: "Cover page",
            hint: "Project name, client, hero image",
            defaultChecked: true,
          },
          {
            key: "library",
            label: "Library gallery",
            hint: `All images (${images.length}) in a paginated grid`,
            defaultChecked: true,
          },
          {
            key: "moodboards",
            label: "Moodboards",
            hint: "One page per moodboard with composition preserved",
            defaultChecked: true,
          },
          {
            key: "floorplans",
            label: "Floorplans & pins",
            hint: "Each plan with numbered pin notes",
            defaultChecked: false,
          },
          {
            key: "products",
            label: "Products & specs",
            hint: "Spec table with vendor, SKU, price",
            defaultChecked: false,
          },
        ]}
        onExport={handleExportProjectPDF}
      />

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        type="project"
        targetId={projectId}
        label={`project — ${project.name}`}
      />

      <CollaboratorsDialog
        open={collabOpen}
        onOpenChange={setCollabOpen}
        projectId={projectId}
        projectName={project.name}
      />

      <TextPromptDialog
        open={!!promptDialog}
        onOpenChange={(o) => !o && setPromptDialog(null)}
        title={promptDialog?.title || ""}
        description={promptDialog?.description}
        initial={promptDialog?.initial || ""}
        placeholder={promptDialog?.placeholder}
        submitLabel={promptDialog?.submitLabel || "Save"}
        onSubmit={promptDialog?.onSubmit}
        testid={promptDialog?.testid || "text-prompt"}
      />

      <RoomMoodboardBuilderDialog
        open={!!roomMoodboardFor}
        onOpenChange={(o) => !o && setRoomMoodboardFor(null)}
        projectId={projectId}
        room={roomMoodboardFor}
        imageCount={roomMoodboardFor ? roomImageCount(roomMoodboardFor.id) : 0}
        onCreated={() => {
          /* moodboards list refreshes lazily when tab is opened */
        }}
      />
    </div>
    </DropZone>
  );
}

function EmptyLibrary() {
  return (
    <div className="border border-dashed border-stone-200 rounded-2xl py-20 text-center">
      <p className="text-stone-500">
        No images yet. Upload some — they'll be auto-categorized and tagged.
      </p>
    </div>
  );
}

// ------ Room chip row (manual + legacy) ------
function RoomChipRow({
  rooms,
  selected,
  onToggle,
  onClear,
  onAddRoom,
  onRenameRoom,
  onDeleteRoom,
  onBuildMoodboard,
  draggedOverRoom,
  onRoomDragOver,
  onRoomDragLeave,
  onRoomDrop,
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 mb-6 pb-4 border-b border-stone-100"
      data-testid="library-room-filter"
    >
      <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500 mr-1">
        Room
      </span>
      <button
        type="button"
        onClick={onClear}
        data-testid="room-chip-all"
        className={`px-3 py-1 rounded-full text-xs transition-colors ${
          selected.size === 0
            ? "bg-stone-900 text-white"
            : "bg-stone-100 text-stone-700 hover:bg-stone-200"
        }`}
      >
        All rooms
      </button>
      {rooms.map((r) =>
        r.kind === "canonical" ? (
          <CanonicalRoomChip
            key={r.key}
            chip={r}
            active={selected.has(r.key)}
            onToggle={() => onToggle(r.key)}
            onRename={() => onRenameRoom({ id: r.id, name: r.name })}
            onDelete={() => onDeleteRoom({ id: r.id, name: r.name })}
            onBuild={() => onBuildMoodboard({ id: r.id, name: r.name })}
            draggedOver={draggedOverRoom === r.id}
            onDragOver={(e) => onRoomDragOver(e, r)}
            onDragLeave={onRoomDragLeave}
            onDrop={(e) => onRoomDrop(e, r)}
          />
        ) : (
          <button
            key={r.key}
            type="button"
            onClick={() => onToggle(r.key)}
            data-testid={`room-chip-legacy-${r.legacyKey.replace(/\s+/g, "-")}`}
            className={`px-3 py-1 rounded-full text-xs transition-colors flex items-center gap-1.5 ${
              selected.has(r.key)
                ? "bg-stone-900 text-white"
                : "bg-stone-100 text-stone-700 hover:bg-stone-200"
            }`}
            title="Auto-detected from images (not a manual room)"
          >
            <span>{r.label}</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                selected.has(r.key)
                  ? "bg-white/20 text-white"
                  : "bg-stone-200/80 text-stone-600"
              }`}
            >
              {r.count}
            </span>
          </button>
        ),
      )}
      <button
        type="button"
        onClick={onAddRoom}
        data-testid="add-room-button"
        className="px-3 py-1 rounded-full text-xs bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors flex items-center gap-1"
        title="Add a new room"
      >
        <Plus className="w-3 h-3" strokeWidth={2} />
        Add room
      </button>
    </div>
  );
}

function CanonicalRoomChip({
  chip,
  active,
  onToggle,
  onRename,
  onDelete,
  onBuild,
  draggedOver,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const safeKey = chip.name.toLowerCase().replace(/\s+/g, "-");
  return (
    <div
      className={`relative group inline-flex items-stretch rounded-full transition-all ${
        draggedOver
          ? "ring-2 ring-stone-900 ring-offset-2 ring-offset-white scale-105"
          : ""
      }`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={`room-chip-wrap-${safeKey}`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`room-chip-${safeKey}`}
        className={`pl-3 pr-1 py-1 rounded-l-full text-xs transition-colors flex items-center gap-1.5 ${
          active
            ? "bg-stone-900 text-white"
            : "bg-stone-100 text-stone-700 hover:bg-stone-200"
        }`}
      >
        <Home className="w-3 h-3" strokeWidth={1.6} />
        <span>{chip.label}</span>
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded-full ${
            active ? "bg-white/20 text-white" : "bg-stone-200/80 text-stone-600"
          }`}
        >
          {chip.count}
        </span>
      </button>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={`room-chip-menu-${safeKey}`}
            onClick={(e) => e.stopPropagation()}
            className={`px-1.5 rounded-r-full text-xs transition-colors ${
              active
                ? "bg-stone-900 text-white hover:bg-stone-800"
                : "bg-stone-100 text-stone-600 hover:bg-stone-200"
            }`}
            title="Room options"
          >
            <ChevronDown className="w-3 h-3" strokeWidth={2.2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
            Room
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onBuild} data-testid={`room-build-mb-${safeKey}`}>
            <LayoutGrid className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            Build moodboard…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onRename} data-testid={`room-rename-${safeKey}`}>
            <Pencil className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setConfirmDelete(true)}
            data-testid={`room-delete-${safeKey}`}
            className="text-red-600 focus:text-red-700"
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            Delete room
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              Delete "{chip.label}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The room will be removed. The {chip.count} linked file
              {chip.count === 1 ? "" : "s"} will be unlinked but not deleted —
              you can re-link them to another room any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-red-600 hover:bg-red-700"
              data-testid={`room-delete-confirm-${safeKey}`}
              onClick={() => {
                setConfirmDelete(false);
                onDelete();
              }}
            >
              Delete room
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ------ Tabs orchestration ------
const ADDABLE_TAB_TYPES = [
  { key: "library", label: "Library", help: "Image & file gallery with categories" },
  { key: "moodboards", label: "Moodboards", help: "Visual composition boards" },
  { key: "floorplans", label: "Floorplans", help: "Plans with pinned references" },
  { key: "products", label: "Products", help: "Spec sheet (multiple allowed)" },
  { key: "other", label: "Other", help: "Free-form notes / generic tab" },
];

const TAB_DEFAULT_NAMES = {
  library: "Library",
  moodboards: "Moodboards",
  floorplans: "Floorplans",
  products: "Products",
  other: "Notes",
};

function ProjectTabs(props) {
  const confirm = useConfirm();
  const {
    project,
    projectId,
    onProjectChange,
    images,
    filter,
    setFilter,
    uploadCategory,
    setUploadCategory,
    projectCategories,
    projectRooms,
    roomFilter,
    toggleRoom,
    clearRooms,
    filteredImages,
    draggedOverPill,
    onPillDragOver,
    onPillDragLeave,
    onPillDrop,
    handleAddCategory,
    handleRenameCategory,
    handleDeleteCategory,
    handleMoveImage,
    handleMoveImageToRoom,
    handleUploaded,
    setSelected,
    handleDeleteImage,
    selected,
    pendingDelete,
    setPendingDelete,
    handleUpdated,
    handleDeletedImage,
    refreshImages,
    handleAddRoom,
    handleRenameRoom,
    handleDeleteRoom,
    handleBuildRoomMoodboard,
    draggedOverRoom,
    onRoomDragOver,
    onRoomDragLeave,
    onRoomDrop,
  } = props;

  const tabs = useMemo(() => project?.tabs || [], [project?.tabs]);
  const [activeId, setActiveId] = useState(tabs[0]?.id || "");
  const [addOpen, setAddOpen] = useState(false);
  const [renameTab, setRenameTab] = useState(null); // tab object being renamed
  const [renameValue, setRenameValue] = useState("");
  const [addType, setAddType] = useState("products");
  const [addName, setAddName] = useState("");
  const [busy, setBusy] = useState(false);
  // Tab drag-to-reorder
  const [dragId, setDragId] = useState(null);
  const [dropIdx, setDropIdx] = useState(null); // insertion index (0..tabs.length)

  function handleTabDragStart(e, t) {
    setDragId(t.id);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/x-atelier-tab", t.id);
    } catch {}
  }
  function handleTabDragOver(e, idx) {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const before = e.clientX < rect.left + rect.width / 2;
    setDropIdx(before ? idx : idx + 1);
  }
  function handleTabDragEnd() {
    setDragId(null);
    setDropIdx(null);
  }
  async function handleTabDrop() {
    if (!dragId || dropIdx === null) {
      handleTabDragEnd();
      return;
    }
    const fromIdx = tabs.findIndex((t) => t.id === dragId);
    if (fromIdx < 0) return handleTabDragEnd();
    // Compute target index after removing the source first
    let toIdx = dropIdx;
    if (fromIdx < toIdx) toIdx -= 1;
    if (fromIdx === toIdx) return handleTabDragEnd();
    const next = tabs.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    const ids = next.map((t) => t.id);
    handleTabDragEnd();
    try {
      const updated = await reorderTabs(projectId, ids);
      onProjectChange(updated);
    } catch (err) {
      toast.error("Failed to reorder tabs");
    }
  }

  // Keep the active tab valid as the project's tab list changes
  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.find((t) => t.id === activeId)) {
      setActiveId(tabs[0].id);
    }
  }, [tabs, activeId]);

  const existingTypes = useMemo(
    () => new Set(tabs.map((t) => t.type)),
    [tabs]
  );

  function openAdd() {
    // Pick a sensible default add-type that's still allowed
    const firstAllowed =
      ADDABLE_TAB_TYPES.find(
        (t) => !["library", "moodboards", "floorplans"].includes(t.key) || !existingTypes.has(t.key)
      ) || ADDABLE_TAB_TYPES[3];
    setAddType(firstAllowed.key);
    setAddName(TAB_DEFAULT_NAMES[firstAllowed.key] || "");
    setAddOpen(true);
  }

  async function submitAdd(e) {
    e?.preventDefault?.();
    const trimmed = (addName || "").trim();
    if (!trimmed) {
      toast.error("Please name the tab");
      return;
    }
    setBusy(true);
    try {
      const updated = await addTab(projectId, { name: trimmed, type: addType });
      onProjectChange(updated);
      // Activate the newly added tab
      const created = updated.tabs?.[updated.tabs.length - 1];
      if (created?.id) setActiveId(created.id);
      setAddOpen(false);
      toast.success("Tab added");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to add tab";
      toast.error(typeof msg === "string" ? msg : "Failed to add tab");
    } finally {
      setBusy(false);
    }
  }

  async function submitRename(e) {
    e?.preventDefault?.();
    if (!renameTab) return;
    const next = (renameValue || "").trim();
    if (!next) {
      toast.error("Tab name can't be empty");
      return;
    }
    if (next === renameTab.name) {
      setRenameTab(null);
      return;
    }
    setBusy(true);
    try {
      const updated = await updateTab(projectId, renameTab.id, { name: next });
      onProjectChange(updated);
      setRenameTab(null);
      toast.success("Tab renamed");
    } catch (err) {
      toast.error("Failed to rename tab");
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteTab(t) {
    if (tabs.length <= 1) {
      toast.error("A project must keep at least one tab");
      return;
    }
    const proceed = await confirm({
      title: `Delete the "${t.name}" tab?`,
      description:
        "Its data (images / boards / products) is preserved but you'll need to add the tab back to see it.",
      confirmLabel: "Delete tab",
      destructive: true,
    });
    if (!proceed) return;
    try {
      const updated = await deleteTab(projectId, t.id);
      onProjectChange(updated);
      toast.success("Tab removed");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to delete tab";
      toast.error(typeof msg === "string" ? msg : "Failed to delete tab");
    }
  }

  if (!tabs.length) return null;

  return (
    <Tabs value={activeId} onValueChange={setActiveId} className="w-full">
      <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="workspace-tabs-bar">
        <TabsList
          className="bg-stone-100/80 rounded-full p-1"
          data-testid="workspace-tabs"
          onDrop={(e) => {
            e.preventDefault();
            handleTabDrop();
          }}
          onDragOver={(e) => {
            if (dragId) e.preventDefault();
          }}
        >
          {tabs.map((t, idx) => (
            <TabTriggerWithMenu
              key={t.id}
              tab={t}
              index={idx}
              active={activeId === t.id}
              canDelete={tabs.length > 1}
              isDragging={dragId === t.id}
              dropBefore={dragId && dropIdx === idx && dragId !== t.id}
              dropAfter={
                dragId && dropIdx === idx + 1 && dragId !== t.id && idx === tabs.length - 1
              }
              onDragStart={(e) => handleTabDragStart(e, t)}
              onDragOver={(e) => handleTabDragOver(e, idx)}
              onDragEnd={handleTabDragEnd}
              onRename={() => {
                setRenameValue(t.name);
                setRenameTab(t);
              }}
              onDelete={() => handleDeleteTab(t)}
            />
          ))}
        </TabsList>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={openAdd}
          data-testid="add-tab-button"
          className="rounded-full text-stone-600 hover:text-stone-900"
        >
          <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
          Add tab
        </Button>
      </div>

      {tabs.map((t) => (
        <TabsContent key={t.id} value={t.id}>
          {t.type === "library" && (
            <LibraryView
              projectId={projectId}
              project={project}
              filter={filter}
              setFilter={setFilter}
              uploadCategory={uploadCategory}
              setUploadCategory={setUploadCategory}
              projectCategories={projectCategories}
              projectRooms={projectRooms}
              roomFilter={roomFilter}
              toggleRoom={toggleRoom}
              clearRooms={clearRooms}
              filteredImages={filteredImages}
              draggedOverPill={draggedOverPill}
              onPillDragOver={onPillDragOver}
              onPillDragLeave={onPillDragLeave}
              onPillDrop={onPillDrop}
              handleAddCategory={handleAddCategory}
              handleRenameCategory={handleRenameCategory}
              handleDeleteCategory={handleDeleteCategory}
              handleMoveImage={handleMoveImage}
              handleMoveImageToRoom={handleMoveImageToRoom}
              handleUploaded={handleUploaded}
              setSelected={setSelected}
              handleDeleteImage={handleDeleteImage}
              selected={selected}
              pendingDelete={pendingDelete}
              setPendingDelete={setPendingDelete}
              handleUpdated={handleUpdated}
              handleDeletedImage={handleDeletedImage}
              handleAddRoom={handleAddRoom}
              handleRenameRoom={handleRenameRoom}
              handleDeleteRoom={handleDeleteRoom}
              handleBuildRoomMoodboard={handleBuildRoomMoodboard}
              draggedOverRoom={draggedOverRoom}
              onRoomDragOver={onRoomDragOver}
              onRoomDragLeave={onRoomDragLeave}
              onRoomDrop={onRoomDrop}
            />
          )}
          {t.type === "moodboards" && (
            <MoodboardsTab projectId={projectId} images={images} />
          )}
          {t.type === "floorplans" && (
            <FloorplansTab
              projectId={projectId}
              images={images}
              onImagesChanged={refreshImages}
            />
          )}
          {t.type === "products" && (
            <ProductsTab projectId={projectId} tabId={t.id} tabName={t.name} images={images} />
          )}
          {t.type === "other" && (
            <OtherTab
              projectId={projectId}
              tab={t}
              onProjectChange={onProjectChange}
            />
          )}
        </TabsContent>
      ))}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-tab-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add a tab</DialogTitle>
            <DialogDescription className="text-stone-500">
              Library, Moodboards & Floorplans are single-use. Products & Other can be added multiple times.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-2 block">
                Type
              </label>
              <div className="grid grid-cols-2 gap-2" data-testid="add-tab-type-grid">
                {ADDABLE_TAB_TYPES.map((opt) => {
                  const isSingle = ["library", "moodboards", "floorplans"].includes(
                    opt.key
                  );
                  const disabled = isSingle && existingTypes.has(opt.key);
                  return (
                    <button
                      type="button"
                      key={opt.key}
                      disabled={disabled}
                      onClick={() => {
                        setAddType(opt.key);
                        setAddName(TAB_DEFAULT_NAMES[opt.key] || "");
                      }}
                      data-testid={`add-tab-type-${opt.key}`}
                      className={`text-left p-3 rounded-xl border transition-all ${
                        disabled
                          ? "border-stone-100 bg-stone-50 opacity-50 cursor-not-allowed"
                          : addType === opt.key
                          ? "border-stone-900 bg-stone-900 text-white"
                          : "border-stone-200 hover:border-stone-400 bg-white"
                      }`}
                    >
                      <p className="font-medium text-sm">{opt.label}</p>
                      <p
                        className={`text-xs mt-0.5 ${
                          addType === opt.key ? "text-stone-300" : "text-stone-500"
                        }`}
                      >
                        {disabled ? "Already added" : opt.help}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-stone-500 mb-2 block">
                Name
              </label>
              <Input
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="e.g. Lighting, Furniture, Notes"
                data-testid="add-tab-name-input"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setAddOpen(false)}
                disabled={busy}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="add-tab-submit"
                className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add tab
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTab} onOpenChange={(o) => !o && setRenameTab(null)}>
        <DialogContent data-testid="rename-tab-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Rename "{renameTab?.name}"
            </DialogTitle>
            <DialogDescription className="text-stone-500">
              Pick a new label for this tab. Only the name changes — its contents are untouched.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRename} className="space-y-4 mt-2">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              data-testid="rename-tab-input"
              autoFocus
            />
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRenameTab(null)}
                disabled={busy}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="rename-tab-submit"
                className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}

function TabTriggerWithMenu({
  tab,
  active,
  canDelete,
  isDragging,
  dropBefore,
  dropAfter,
  onRename,
  onDelete,
  onDragStart,
  onDragOver,
  onDragEnd,
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`relative group inline-flex items-center transition-opacity ${
        isDragging ? "opacity-40" : "opacity-100"
      }`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      data-testid={`tab-wrapper-${tab.id}`}
    >
      {dropBefore && (
        <span
          className="absolute -left-1 top-1 bottom-1 w-1 rounded-full bg-stone-900"
          data-testid={`tab-drop-indicator-before-${tab.id}`}
        />
      )}
      <TabsTrigger
        value={tab.id}
        className="rounded-full px-5 pr-8 data-[state=active]:bg-stone-900 data-[state=active]:text-white cursor-grab active:cursor-grabbing"
        data-testid={`tab-${tab.type}-${tab.id}`}
      >
        {tab.name}
      </TabsTrigger>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={`tab-menu-${tab.id}`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            className={`absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center transition-opacity ${
              active ? "text-white/80 hover:text-white opacity-100" : "text-stone-500 hover:text-stone-900 opacity-0 group-hover:opacity-100"
            } ${open ? "opacity-100" : ""}`}
            title="Tab options"
          >
            <ChevronDown className="w-3 h-3" strokeWidth={2.2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
            {tab.type}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onRename} data-testid={`tab-rename-${tab.id}`}>
            <Pencil className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onDelete}
            disabled={!canDelete}
            data-testid={`tab-delete-${tab.id}`}
            className="text-red-600 focus:text-red-700"
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            Delete tab
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dropAfter && (
        <span
          className="absolute -right-1 top-1 bottom-1 w-1 rounded-full bg-stone-900"
          data-testid={`tab-drop-indicator-after-${tab.id}`}
        />
      )}
    </div>
  );
}

// ------ Library view (extracted so it can live inside a dynamic tab) ------
function LibraryView({
  projectId,
  project,
  filter,
  setFilter,
  uploadCategory,
  setUploadCategory,
  projectCategories,
  projectRooms,
  roomFilter,
  toggleRoom,
  clearRooms,
  filteredImages,
  draggedOverPill,
  onPillDragOver,
  onPillDragLeave,
  onPillDrop,
  handleAddCategory,
  handleRenameCategory,
  handleDeleteCategory,
  handleMoveImage,
  handleMoveImageToRoom,
  handleUploaded,
  setSelected,
  handleDeleteImage,
  selected,
  pendingDelete,
  setPendingDelete,
  handleUpdated,
  handleDeletedImage,
  handleAddRoom,
  handleRenameRoom,
  handleDeleteRoom,
  handleBuildRoomMoodboard,
  draggedOverRoom,
  onRoomDragOver,
  onRoomDragLeave,
  onRoomDrop,
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center" data-testid="library-filter">
          <CategoryPill
            value="all"
            label="All"
            active={filter === "all"}
            onClick={() => setFilter("all")}
            draggedOver={false}
            editable={false}
          />
          {projectCategories.map((c) => (
            <CategoryPill
              key={c}
              value={c}
              label={categoryLabel(c)}
              active={filter === c}
              onClick={() => setFilter(c)}
              draggedOver={draggedOverPill === c}
              onDragOver={(e) => onPillDragOver(e, c)}
              onDragLeave={onPillDragLeave}
              onDrop={(e) => onPillDrop(e, c)}
              editable={c !== "inspiration"}
              onRename={() => handleRenameCategory(c)}
              onDelete={() => handleDeleteCategory(c)}
            />
          ))}
          <button
            type="button"
            onClick={handleAddCategory}
            data-testid="add-category-button"
            className="px-3 py-1.5 rounded-full text-sm bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors flex items-center gap-1"
            title="Add category"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
            Add
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={uploadCategory} onValueChange={setUploadCategory}>
            <SelectTrigger className="w-[150px] rounded-full" data-testid="upload-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {projectCategories.map((c) => (
                <SelectItem key={c} value={c}>
                  {categoryLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ImageUploader
            projectId={projectId}
            category={uploadCategory}
            onUploaded={handleUploaded}
          />
          <UrlImportDialog
            projectId={projectId}
            category={uploadCategory}
            onUploaded={handleUploaded}
            trigger={
              <Button
                variant="outline"
                className="rounded-full"
                data-testid="open-url-import-button"
              >
                <Link2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                From URL
              </Button>
            }
          />
        </div>
      </div>

      <RoomChipRow
        rooms={projectRooms}
        selected={roomFilter}
        onToggle={toggleRoom}
        onClear={clearRooms}
        onAddRoom={handleAddRoom}
        onRenameRoom={handleRenameRoom}
        onDeleteRoom={handleDeleteRoom}
        onBuildMoodboard={handleBuildRoomMoodboard}
        draggedOverRoom={draggedOverRoom}
        onRoomDragOver={onRoomDragOver}
        onRoomDragLeave={onRoomDragLeave}
        onRoomDrop={onRoomDrop}
      />

      {filteredImages.length === 0 ? (
        <EmptyLibrary />
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4" data-testid="image-masonry">
          {filteredImages.map((img, idx) => (
            <ImageCard
              key={img.id}
              image={img}
              index={idx}
              onClick={setSelected}
              onRequestDelete={handleDeleteImage}
              onMove={handleMoveImage}
              onMoveRoom={handleMoveImageToRoom}
              categories={projectCategories}
              rooms={project?.rooms || []}
              draggable
            />
          ))}
        </div>
      )}

      <ImageDetailDialog
        image={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onUpdated={handleUpdated}
      />
      <ConfirmDeleteImageDialog
        image={pendingDelete}
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        onDeleted={handleDeletedImage}
      />
    </>
  );
}

// ------ Other / Notes tab ------
function OtherTab({ projectId, tab, onProjectChange }) {
  const [value, setValue] = useState(tab.notes || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    setValue(tab.notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.id]); // reset when switching tabs

  useEffect(() => {
    if ((tab.notes || "") === value) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const updated = await updateTab(projectId, tab.id, { notes: value });
        onProjectChange(updated);
        setSavedAt(new Date());
      } catch (err) {
        toast.error("Failed to save notes");
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <p className="text-stone-500 text-sm flex items-center gap-2">
          <StickyNote className="w-4 h-4" strokeWidth={1.5} />
          Free-form notes for this tab. Auto-saves.
        </p>
        <span className="text-xs text-stone-400" data-testid={`other-tab-status-${tab.id}`}>
          {saving ? "Saving…" : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : ""}
        </span>
      </div>
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={`other-tab-notes-${tab.id}`}
        placeholder="Jot down meeting notes, to-dos, links, anything that doesn't fit the other tabs…"
        className="min-h-[400px] rounded-2xl bg-stone-50 border-stone-200 text-stone-800 leading-relaxed"
      />
    </div>
  );
}




function CategoryPill({
  value,
  label,
  active,
  onClick,
  draggedOver,
  onDragOver,
  onDragLeave,
  onDrop,
  editable,
  onRename,
  onDelete,
}) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={`filter-${value}`}
      data-testid-pill={value}
      className={`relative group flex items-center rounded-full transition-all ${
        draggedOver
          ? "ring-2 ring-stone-900 ring-offset-2 ring-offset-stone-50 scale-105"
          : ""
      } ${
        active
          ? "bg-stone-900 text-white"
          : "bg-stone-100 text-stone-700 hover:bg-stone-200"
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className="px-4 py-1.5 text-sm rounded-full"
      >
        {label}
      </button>
      {editable && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              data-testid={`filter-menu-${value}`}
              className={`pr-2 pl-0.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                active ? "text-white/80 hover:text-white" : "text-stone-500 hover:text-stone-900"
              }`}
              title="Edit category"
            >
              <Pencil className="w-3 h-3" strokeWidth={2} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              data-testid={`filter-rename-${value}`}
              onClick={onRename}
            >
              <Pencil className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
              Rename
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid={`filter-delete-${value}`}
              onClick={onDelete}
              className="text-red-600 focus:text-red-700"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

// ------ Moodboards ------
function MoodboardsTab({ projectId, images }) {
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function load() {
    setLoading(true);
    try {
      setList(await fetchMoodboards(projectId));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const created = await createMoodboard({
        project_id: projectId,
        name,
        items: [],
      });
      setName("");
      setOpen(false);
      nav(`/project/${projectId}/moodboard/${created.id}`);
    } catch {
      toast.error("Failed to create moodboard");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(mb, e) {
    e.preventDefault();
    e.stopPropagation();
    const ok = await confirm({
      title: `Delete "${mb.name}"?`,
      description:
        "The moodboard layout will be removed. The linked source images stay in your library.",
      confirmLabel: "Delete moodboard",
      destructive: true,
    });
    if (!ok) return;
    await deleteMoodboard(mb.id);
    setList(list.filter((x) => x.id !== mb.id));
    toast.success("Moodboard deleted");
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-stone-500 text-sm">
          Drag, arrange, and play with combinations on an infinite canvas.
        </p>
        <div className="flex items-center gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-moodboard-button"
                className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                New moodboard
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">New moodboard</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Living room mood"
                  data-testid="moodboard-name-input"
                  required
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={busy}
                    data-testid="submit-moodboard-button"
                    className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                  >
                    {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create & open
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <AiMoodboardDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        projectId={projectId}
        images={images}
        onCreated={(mb) => {
          setList((prev) => [mb, ...prev]);
          nav(`/project/${projectId}/moodboard/${mb.id}`);
        }}
      />

      {loading ? (
        <div className="py-16 text-center text-stone-400">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-stone-200 rounded-2xl py-20 text-center text-stone-500">
          No moodboards yet. Create your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="moodboards-grid">
          {list.map((mb, i) => (
            <motion.div
              key={mb.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
            >
              <Link
                to={`/project/${projectId}/moodboard/${mb.id}`}
                data-testid={`moodboard-tile-${mb.id}`}
                className="group block aspect-[4/3] relative rounded-2xl overflow-hidden bg-[#F7F7F5] dotted-canvas"
              >
                <MoodboardPreview moodboard={mb} images={images} />
                <button
                  type="button"
                  onClick={(e) => handleDelete(mb, e)}
                  data-testid={`delete-moodboard-${mb.id}`}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-stone-700 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white/80 to-transparent">
                  <h4 className="font-display text-lg text-stone-900">{mb.name}</h4>
                  <p className="text-xs text-stone-500">
                    {mb.items?.length || 0} item{mb.items?.length === 1 ? "" : "s"}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function MoodboardPreview({ moodboard, images }) {
  const items = (moodboard.items || []).slice(0, 6);
  const byId = Object.fromEntries(images.map((i) => [i.id, i]));
  return (
    <div className="absolute inset-0 p-4">
      {items.length === 0 ? (
        <div className="w-full h-full flex items-center justify-center text-stone-400 text-sm">
          Empty board
        </div>
      ) : (
        <div className="relative w-full h-full">
          {items.map((it, idx) => {
            const img = byId[it.image_id];
            if (!img) return null;
            const left = 8 + (idx % 3) * 30;
            const top = 10 + Math.floor(idx / 3) * 38;
            return (
              <img
                key={idx}
                src={img.data}
                alt=""
                className="absolute w-1/3 aspect-square object-cover rounded-md shadow-sm border border-white"
                style={{ left: `${left}%`, top: `${top}%`, transform: `rotate(${(idx % 2 ? -1 : 1) * 2}deg)` }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ------ Floorplans ------
function FloorplansTab({ projectId, images, onImagesChanged }) {
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPicker, setOpenPicker] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      setList(await fetchFloorplans(projectId));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const floorplanImgs = images.filter((i) => i.category === "floorplan");

  async function attach(imgId) {
    setBusy(true);
    try {
      const fp = await createFloorplan({ project_id: projectId, image_id: imgId });
      setList([fp, ...list]);
      setOpenPicker(false);
      toast.success("Floorplan added");
    } catch {
      toast.error("Failed to add floorplan");
    } finally {
      setBusy(false);
    }
  }

  async function uploadFloorplan(file) {
    setBusy(true);
    try {
      const raw = await readFileAsDataURL(file);
      const small = await downsizeImage(raw, 2000, 0.9);
      const img = await uploadImage(projectId, {
        project_id: projectId,
        data: small,
        filename: file.name,
        category: "floorplan",
        title: "Floorplan",
      });
      onImagesChanged?.();
      const fp = await createFloorplan({ project_id: projectId, image_id: img.id });
      setList((prev) => [fp, ...prev]);
      toast.success("Floorplan added");
    } catch {
      toast.error("Failed to upload floorplan");
    } finally {
      setBusy(false);
    }
  }

  async function handleUpdate(fp, pins) {
    const res = await updateFloorplan(fp.id, { pins });
    setList((prev) => prev.map((x) => (x.id === fp.id ? res : x)));
  }

  async function handleDelete(fp) {
    const ok = await confirm({
      title: "Delete this floorplan?",
      description:
        "Any pins on this plan will be lost. Linked images remain in your library.",
      confirmLabel: "Delete floorplan",
      destructive: true,
    });
    if (!ok) return;
    await deleteFloorplan(fp.id);
    setList(list.filter((x) => x.id !== fp.id));
    toast.success("Floorplan deleted");
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <p className="text-stone-500 text-sm">
          Click on a plan to drop a pin and link it to any image in your library.
        </p>
        <div className="flex items-center gap-2">
          <Dialog open={openPicker} onOpenChange={setOpenPicker}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="rounded-full"
                data-testid="attach-floorplan-button"
              >
                <MapIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Use existing image
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  Pick a floorplan image
                </DialogTitle>
              </DialogHeader>
              {floorplanImgs.length === 0 ? (
                <p className="text-stone-500 py-8 text-center">
                  No floorplan images in this project's library yet.
                </p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pt-2">
                  {floorplanImgs.map((img) => (
                    <button
                      key={img.id}
                      onClick={() => attach(img.id)}
                      disabled={busy}
                      data-testid={`pick-floorplan-${img.id}`}
                      className="aspect-square rounded-lg overflow-hidden bg-stone-100 hover:ring-2 hover:ring-stone-900 transition-all"
                    >
                      <img
                        src={img.data}
                        alt={img.filename}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
          <FloorplanUpload onSelect={uploadFloorplan} busy={busy} />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-stone-400">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-stone-200 rounded-2xl py-20 text-center text-stone-500">
          No floorplans yet. Upload one or pick one from your library.
        </div>
      ) : (
        <div className="space-y-10" data-testid="floorplans-list">
          {list.map((fp) => {
            const img = images.find((i) => i.id === fp.image_id);
            if (!img) return null;
            return (
              <FloorplanEditor
                key={fp.id}
                floorplan={fp}
                image={img}
                images={images}
                projectId={projectId}
                onChange={(pins) => handleUpdate(fp, pins)}
                onDelete={() => handleDelete(fp)}
                onUploaded={(newImg) => {
                  onImagesChanged?.(newImg);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function FloorplanUpload({ onSelect, busy }) {
  const ref = { current: null };
  return (
    <>
      <input
        ref={(el) => (ref.current = el)}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="upload-floorplan-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelect(f);
          e.target.value = "";
        }}
      />
      <Button
        onClick={() => ref.current?.click()}
        disabled={busy}
        data-testid="upload-floorplan-button"
        className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />}
        Upload floorplan
      </Button>
    </>
  );
}

// ------ Products ------
function ProductsTab({ projectId, tabId, tabName, images }) {
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct());
  const [busy, setBusy] = useState(false);

  function emptyProduct() {
    return {
      name: "",
      supplier: "",
      price: "",
      currency: "USD",
      lead_time: "",
      notes: "",
      image_id: "",
      status: "considering",
    };
  }

  async function load() {
    setLoading(true);
    try {
      setList(await fetchProducts(projectId, tabId));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, tabId]);

  function startNew() {
    setEditing(null);
    setForm(emptyProduct());
    setOpen(true);
  }

  function startEdit(p) {
    setEditing(p);
    setForm({
      name: p.name || "",
      supplier: p.supplier || "",
      price: p.price ?? "",
      currency: p.currency || "USD",
      lead_time: p.lead_time || "",
      notes: p.notes || "",
      image_id: p.image_id || "",
      status: p.status || "considering",
    });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        project_id: projectId,
        tab_id: tabId,
        ...form,
        price: form.price === "" ? 0 : Number(form.price),
      };
      if (editing) {
        const res = await updateProduct(editing.id, payload);
        setList(list.map((x) => (x.id === editing.id ? res : x)));
        toast.success("Product updated");
      } else {
        const res = await createProduct(payload);
        setList([res, ...list]);
        toast.success("Product added");
      }
      setOpen(false);
    } catch {
      toast.error("Failed to save product");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(p) {
    const ok = await confirm({
      title: `Delete "${p.name}"?`,
      description: "This removes the product spec from this list.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await deleteProduct(p.id);
    setList(list.filter((x) => x.id !== p.id));
    toast.success("Deleted");
  }

  const productImages = images.filter((i) =>
    ["product", "material"].includes(i.category)
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-stone-500 text-sm">
          {tabName ? `${tabName} — ` : ""}Spec sheet for furniture, materials, and finishes.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={startNew}
              data-testid="new-product-button"
              className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Add product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {editing ? "Edit product" : "Add product"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              <Input
                placeholder="Name"
                value={form.name}
                required
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="product-name-input"
              />
              <Input
                placeholder="Supplier"
                value={form.supplier}
                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                data-testid="product-supplier-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Price"
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="product-price-input"
                />
                <Select
                  value={form.currency}
                  onValueChange={(v) => setForm({ ...form, currency: v })}
                >
                  <SelectTrigger data-testid="product-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["USD", "EUR", "GBP", "AED", "INR"].map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="Lead time"
                value={form.lead_time}
                onChange={(e) => setForm({ ...form, lead_time: e.target.value })}
                data-testid="product-lead-input"
              />
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger data-testid="product-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="considering">Considering</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={form.image_id || "none"}
                onValueChange={(v) => setForm({ ...form, image_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="product-image-select">
                  <SelectValue placeholder="Link an image" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No image</SelectItem>
                  {productImages.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.title || i.filename || categoryLabel(i.category)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                placeholder="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="product-notes-input"
              />
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="submit-product-button"
                  className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                >
                  {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editing ? "Save" : "Add product"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-16 text-center text-stone-400">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </div>
      ) : list.length === 0 ? (
        <div className="border border-dashed border-stone-200 rounded-2xl py-20 text-center text-stone-500">
          No products yet.
        </div>
      ) : (
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          data-testid="products-grid"
        >
          {list.map((p, i) => {
            const img = images.find((x) => x.id === p.image_id);
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="group relative"
                data-testid={`product-card-${p.id}`}
              >
                <button
                  onClick={() => startEdit(p)}
                  className="block w-full aspect-square overflow-hidden rounded-xl bg-stone-100"
                >
                  {img ? (
                    <img
                      src={img.data}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs">
                      No image
                    </div>
                  )}
                </button>
                <div className="mt-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-stone-900 truncate">{p.name}</p>
                    {p.supplier && (
                      <p className="text-xs text-stone-500 truncate">{p.supplier}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm text-stone-900">
                      {formatPrice(p.price, p.currency)}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-stone-500">
                      {p.status}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(p)}
                  data-testid={`delete-product-${p.id}`}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-stone-700 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
